import { useState, type ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { clsx } from "clsx";
import type { Action } from "@rrid/shared-types";
import { useAuth } from "../lib/auth.js";
import { useCan } from "./Can.js";
import { api } from "../lib/api.js";
import { qk } from "../lib/queryKeys.js";
import { useLiveUpdates, type ConnState } from "../hooks/useLiveUpdates.js";
import { humanizeNotificationKind, userRoleSubtitle } from "../lib/rbacUi.js";
import { DevRoleSwitcher, useDevAuthAvailable } from "./DevRoleSwitcher.js";
import { Badge, Button } from "./ui.js";

interface NotificationRow {
  id: string;
  kind: string;
  message: string;
  read: boolean;
  at: string;
}

// Access to actions *inside* each portal is enforced server-side (every write is re-checked) and
// reflected by each portal's own gating. Operations is the one portal hidden outright (July-8
// FR-N4): a student / QC-in-training has nothing to do there, so the entry point goes away —
// backed by a 403 on every /api/operations route, since hiding nav is never the enforcement.
const NAV: { to: string; label: string; action?: Action }[] = [
  { to: "/preprints", label: "Find a Preprint" },
  { to: "/reviewers", label: "Find Reviewers" },
  { to: "/quality-check", label: "Quality Check" },
  { to: "/operations", label: "Operations", action: "openOperations" },
  // Staff-only status board (same gate as GET /api/admin/status).
  { to: "/admin", label: "System Status", action: "viewAuditTrail" },
];

function ConnPill({ state }: { state: ConnState }): JSX.Element {
  const map: Record<ConnState, { tone: "green" | "amber" | "slate"; label: string }> = {
    live: { tone: "green", label: "Live" },
    connecting: { tone: "slate", label: "Connecting…" },
    reconnecting: { tone: "amber", label: "Reconnecting…" },
  };
  const { tone, label } = map[state];
  return (
    <span className="inline-flex items-center gap-1.5" title="Real-time connection status">
      <span
        className={clsx(
          "h-2 w-2 rounded-full",
          tone === "green" ? "bg-green-500" : tone === "amber" ? "bg-amber-500 animate-pulse" : "bg-slate-400",
        )}
      />
      <span className="text-xs text-slate-500">{label}</span>
    </span>
  );
}

function Notifications(): JSX.Element {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: qk.notifications,
    queryFn: () => api.get<NotificationRow[]>("/notifications"),
    refetchInterval: 20000,
  });
  const markRead = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.notifications }),
  });
  const unread = (data ?? []).filter((n) => !n.read).length;
  return (
    <div className="relative">
      <Button variant="ghost" onClick={() => setOpen((o) => !o)} aria-label={`Notifications (${unread} unread)`}>
        🔔 {unread > 0 && <Badge tone="red">{unread}</Badge>}
      </Button>
      {open && (
        <div className="absolute right-0 z-40 mt-1 max-h-96 w-80 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          {(data ?? []).length === 0 && <p className="p-4 text-sm text-slate-500">No notifications.</p>}
          {(data ?? []).map((n) => (
            <button
              key={n.id}
              onClick={() => markRead.mutate(n.id)}
              className={clsx("block w-full border-b border-slate-100 px-3 py-2 text-left text-sm hover:bg-slate-50", !n.read && "bg-blue-50")}
            >
              <div className="font-medium text-slate-700">{humanizeNotificationKind(n.kind)}</div>
              <div className="text-slate-600">{n.message}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface HealthPayload {
  env?: string;
  service?: string;
  sha?: string;
  buildTime?: string | null;
}

function VersionChip(): JSX.Element | null {
  const { data } = useQuery({
    queryKey: ["deploy-health"],
    queryFn: () => api.get<HealthPayload>("/health"),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  if (!data?.sha || data.sha === "dev") return null;
  const when = data.buildTime
    ? new Date(data.buildTime).toLocaleString("en-US", {
        timeZone: "America/Los_Angeles",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZoneName: "short",
      })
    : null;
  const label = [data.env ?? data.service, data.sha.slice(0, 7), when].filter(Boolean).join(" · ");
  return (
    <span
      className="font-mono text-[11px] text-slate-400"
      title={`Deployed service ${data.service ?? "?"} (${data.env ?? "?"})`}
    >
      {label}
    </span>
  );
}

export function Layout({ children }: { children: ReactNode }): JSX.Element {
  const { user, logout } = useAuth();
  const { state } = useLiveUpdates();
  const devAuth = useDevAuthAvailable();
  const can = useCan();
  const nav = NAV.filter((n) => !n.action || can(n.action));
  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-2.5">
          <NavLink to="/" className="text-lg font-bold tracking-tight text-brand">
            RR<span className="text-slate-400">\</span>ID
          </NavLink>
          <nav className="flex flex-1 gap-1">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  clsx("rounded-md px-3 py-1.5 text-sm font-medium", isActive ? "bg-brand/10 text-brand" : "text-slate-600 hover:bg-slate-100")
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <ConnPill state={state} />
          <Notifications />
          {user && (
            <div className="flex items-center gap-2 border-l border-slate-200 pl-3">
              {devAuth ? (
                <DevRoleSwitcher />
              ) : (
                <div className="text-right">
                  <div className="text-sm font-medium leading-tight">{user.name || user.email}</div>
                  <div className="text-xs text-slate-500">{userRoleSubtitle(user)}</div>
                </div>
              )}
              <Button variant="secondary" onClick={() => void logout()}>
                Sign out
              </Button>
            </div>
          )}
        </div>
      </header>
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-6">{children}</main>
      <footer className="border-t border-slate-100 py-3 text-center">
        <VersionChip />
      </footer>
    </div>
  );
}
